import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod";
import { SiteShell } from "@/components/site/SiteShell";
import { BlogIndex } from "@/components/blog/BlogIndex";
import { SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { publicBlogPostsQuery, publicBlogCategoryCountsQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import type { BlogPostCard } from "@/components/blog/blog-types";

const searchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1).default(1),
});

export const Route = createFileRoute("/blog/")({
  validateSearch: searchSchema,
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    const [list, counts] = await Promise.all([
      context.queryClient.ensureQueryData(publicBlogPostsQuery()),
      context.queryClient.ensureQueryData(publicBlogCategoryCountsQuery()),
    ]);
    return { posts: (list?.posts ?? []) as BlogPostCard[], counts: counts?.counts ?? {} };
  },
  head: ({ loaderData }) => {
    const posts = (loaderData?.posts ?? []) as BlogPostCard[];
    const title = "Shopify Insights: Engineering Notes & Analysis · Shugert";
    const description =
      "Timely writing from the Shugert team: Shopify platform changes, performance research, migration learnings and AI in commerce. Playbooks live in the resource library.";
    return buildHead({
      locale: "en",
      title,
      description,
      path: "/blog",
      altPath: "/blog",
      feeds: [{ href: "/blog/rss.xml", title: "Shugert Insights" }],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": `${SITE_URL}/blog#blog`,
          name: "Shugert Insights",
          description,
          url: `${SITE_URL}/blog`,
          publisher: { "@type": "Organization", "@id": `${SITE_URL}#org`, name: "Shugert" },
        },
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/blog" },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListOrder: "https://schema.org/ItemListOrderDescending",
          numberOfItems: posts.length,
          itemListElement: posts.slice(0, 20).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/blog/${p.slug}`,
            name: p.title,
          })),
        },
      ],
    });
  },
  component: BlogIndexRoute,
});

function BlogIndexRoute() {
  const { posts, counts } = Route.useLoaderData();
  const { page } = Route.useSearch();
  return (
    <SiteShell>
      <BlogIndex posts={posts} counts={counts} page={page} />
    </SiteShell>
  );
}
